import { Component, Inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Data,
  NavigationEnd,
  ResolveEnd,
  Router,
} from '@angular/router';
import {
  I18NextPipe,
  I18NEXT_SERVICE,
  ITranslationService,
} from 'angular-i18next';
import { PrimeNG } from 'primeng/config';
import { filter, map, switchMap } from 'rxjs/operators';
import {
  MxAppInitService,
  MxBreadcrumbService,
  MxCookieConsentService,
  MxLoggingService,
} from './core/services';

@Component({
 standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  appTitle: string;

  routerSubscription: any;

  constructor(
    private primeng: PrimeNG,
    private titleService: Title,
    private breadcrumbService: MxBreadcrumbService,
    private router: Router,
    private route: ActivatedRoute,
    private i18nextPipe: I18NextPipe,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    private appInitService: MxAppInitService,
    private ccService: MxCookieConsentService,
    private loggingService: MxLoggingService
  ) {}

  ngOnInit() {
    if (!this.appInitService.currentConfig) {
      throw new Error(
        'Config has not been loaded. Did you forget to call appInit.loadConfig()?'
      );
    }

    this.translateCookieConsent();

    this.primeng.setTranslation(
      this.i18NextService.getResourceBundle(
        this.i18NextService.language,
        'primeng'
      )
    );

    this.appTitle = this.titleService.getTitle();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
        map((route) => {
          let child = this.route.firstChild;
          while (child.firstChild) {
            child = child.firstChild;
          }
          return child;
        }),
        switchMap((route) => route.data)
      )
      .subscribe((data: Data) => {
        this.breadcrumbService.setItems([
          { label: this.i18nextPipe.transform(`page:${data['title']}`) },
        ]);
        this.titleService.setTitle(
          this.appTitle +
            ' | ' +
            this.i18nextPipe.transform(`page:${data['title']}`)
        );
      });

    this.router.events
      .pipe(filter((event) => event instanceof ResolveEnd))
      .subscribe((event) => {
        const activatedComponent = this.getActivatedComponent(
          (event as ResolveEnd).state.root
        );
        if (activatedComponent) {
          this.loggingService.logAppInsightsPageView(
            `${activatedComponent.name} ${this.getRouteTemplate(
              (event as ResolveEnd).state.root
            )}`,
            (event as ResolveEnd).urlAfterRedirects
          );
        }
      });
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  translateCookieConsent() {
    this.ccService.getConfig().content =
      this.ccService.getConfig().content || {};
    // Override default messages with the translated ones
    this.ccService.getConfig().content.header =
      this.i18nextPipe.transform('cookie:header');
    this.ccService.getConfig().content.message =
      this.i18nextPipe.transform('cookie:message');
    this.ccService.getConfig().content.dismiss =
      this.i18nextPipe.transform('cookie:dismiss');
    this.ccService.getConfig().content.allow =
      this.i18nextPipe.transform('cookie:allow');
    this.ccService.getConfig().content.deny =
      this.i18nextPipe.transform('cookie:deny');
    this.ccService.getConfig().content.link =
      this.i18nextPipe.transform('cookie:link');
    this.ccService.getConfig().content.policy =
      this.i18nextPipe.transform('cookie:policy');

    this.ccService.destroy(); // remove previous cookie bar (with default messages)
    this.ccService.init(this.ccService.getConfig()); // update config with translated messages
  }
}

